import React, { useCallback, useEffect, useState } from 'react';
import { withStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import moment from 'moment-timezone';
import Tooltip from '@mui/material/Tooltip';
import { useLangFile } from 'js/context/LanguageContext';
import IconButton from '@mui/material/IconButton';
import { ChevronLeft, ChevronRight } from '@mui/icons-material';
import Box from '@mui/material/Box';
import { voidFunc } from 'js/constants/PropTypeUtils';
import { blueGrey, grey } from '@mui/material/colors';
import { formatDateAsYearMonthDate } from '../../helpers/DateUtils';
import { DatePicker } from '@mui/x-date-pickers';

const styles = (theme) => ({
  root: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  picker: {
    width: 'auto',
  },
  pickerWrapper: {
    flexShrink: 0,
  },
  pickerInput: {
    cursor: 'pointer',
    padding: '0 8px',
    // color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: 'rgba(0,0,0,0.2)',
    },
    '& fieldset': {
      border: 'none',
      top: 0,
    },
    '& legend': {
      display: 'none',
    },
    '& .MuiPickersInputBase-sectionsContainer': {
      width: 'auto',
      flexGrow: 0,
      padding: '4px 0',
      fontSize: '0.9rem',
    },
    '& input': {
      textAlign: 'center',
      cursor: 'pointer',
      padding: 0,
      fontSize: '0.9rem',
    },
  },
  buttonWrapper: {
    width: 28,
    flexShrink: 0,
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  button: {
    padding: theme.spacing(0.5),
  },
});

const DateHandler = ({ classes, date, dates, handleDateSet }) => {
  const LangFile = useLangFile();

  const [value, setValue] = useState(date);
  const [min, setMin] = useState(null);
  const [max, setMax] = useState(null);

  const [calendarOpen, setCalendarOpen] = useState(false);
  const [hasNext, setHasNext] = useState(false);
  const [hasPrevious, setHasPrevious] = useState(false);

  useEffect(() => {
    if (dates) {
      const currentIndex = dates.indexOf(date);
      const hasNext = Boolean(dates[currentIndex + 1]);
      const hasPrevious = Boolean(dates[currentIndex - 1]);
      setHasNext(hasNext);
      setHasPrevious(hasPrevious);
    }
  }, [date, dates]);

  const leftArrowPress = useCallback(() => {
    const newIndex = dates.indexOf(date) - 1;
    const newDate = dates[newIndex];

    if (newDate) {
      handleDateSet(newDate, false);
    }
  }, [date, dates, handleDateSet]);

  const rightArrowPress = useCallback(() => {
    const newIndex = dates.indexOf(date) + 1;
    const newDate = dates[newIndex];

    if (newDate) {
      handleDateSet(newDate, true);
    }
  }, [date, dates, handleDateSet]);

  useEffect(() => {
    if (dates) {
      setMin(moment(dates[0]).toDate());
      setMax(moment(dates[dates.length - 1]).toDate());
    }
  }, [dates]);

  useEffect(() => {
    if (date) {
      setValue(moment(date).toDate());
    }
  }, [date]);

  const disableDate = useCallback(
    (date) => {
      return dates.indexOf(formatDateAsYearMonthDate(date)) === -1;
    },
    [dates]
  );

  const handleSetCalendarOpen = (val) => () => {
    setCalendarOpen(val);
  };

  const small = window.innerWidth < 768;
  const format = 'DD-MM-YYYY';

  if (!date || !dates || (Array.isArray(dates) && dates.length === 0)) {
    return null;
  }

  return (
    <Box className={classes.root}>
      <Tooltip title={!hasPrevious ? LangFile.DateHandler.noPrevious : LangFile.DateHandler.change}>
        <Box className={classes.buttonWrapper}>
          <IconButton disabled={!hasPrevious} onClick={leftArrowPress} className={classes.button}>
            <ChevronLeft fontSize="small" htmlColor={hasPrevious ? blueGrey['900'] : grey['200']} />
          </IconButton>
        </Box>
      </Tooltip>
      <Tooltip title={calendarOpen ? '' : LangFile.DateHandler.pickDate}>
        <div className={classes.pickerWrapper}>
          <DatePicker
            disableOpenPicker
            open={calendarOpen}
            onOpen={handleSetCalendarOpen(true)}
            onClose={handleSetCalendarOpen(false)}
            slotProps={{
              textField: {
                className: classes.picker,
                slotProps: {
                  inputLabel: { shrink: small },
                  input: { classes: { root: classes.pickerInput } },
                  htmlInput: { readOnly: true, 'data-lpignore': true, autoComplete: 'off' },
                },
                onClick: handleSetCalendarOpen(true),
              },
              desktopPaper: { sx: { maxHeight: 600 } },
            }}
            disableFuture={true}
            disabled={dates.length <= 0}
            referenceDate={moment(value)}
            value={value ? moment(value) : null}
            onChange={(date) => handleDateSet(date)}
            shouldDisableDate={disableDate}
            minDate={min ? moment(min) : undefined}
            maxDate={max ? moment(max) : undefined}
            format={format}
          />
        </div>
      </Tooltip>
      <Tooltip title={!hasNext ? LangFile.DateHandler.noNext : LangFile.DateHandler.change}>
        <Box className={classes.buttonWrapper}>
          <IconButton disabled={!hasNext} onClick={rightArrowPress} className={classes.button}>
            <ChevronRight fontSize="small" htmlColor={hasNext ? blueGrey['900'] : grey['200']} />
          </IconButton>
        </Box>
      </Tooltip>
    </Box>
  );
};

DateHandler.propTypes = {
  date: PropTypes.oneOfType([PropTypes.string, PropTypes.object]),
  dates: PropTypes.array,
  handleDateSet: PropTypes.func,
};

DateHandler.defaultProps = {
  handleDateSet: voidFunc,
  dates: [],
};

export default withStyles(styles)(DateHandler);
